<template>
  <!--
    Teleported out of the panel: a viewer opened to show detail the inline chart
    is too small for should not be confined to a panel that may itself be 450px
    wide. Leaving the panel also escapes its drag transform, which would
    otherwise anchor `position: fixed` to the panel rather than the viewport.
  -->
  <Teleport to="body">
    <div
      ref="root"
      class="image-lightbox"
      role="dialog"
      aria-modal="true"
      :aria-label="alt || 'Image preview'"
      tabindex="-1"
      @keydown.esc.stop="$emit('close')"
      @click.self="$emit('close')"
    >
      <div class="image-lightbox-bar">
        <p class="image-lightbox-title">{{ alt || "Chart" }}</p>

        <div class="image-lightbox-actions">
          <button
            type="button"
            class="image-lightbox-btn"
            title="Zoom out"
            :disabled="zoom <= MIN_ZOOM"
            @click="zoomBy(-ZOOM_STEP)"
          >
            <i class="fas fa-magnifying-glass-minus"></i>
          </button>
          <button
            type="button"
            class="image-lightbox-zoom"
            title="Reset to fit"
            @click="zoom = 100"
          >
            {{ zoom }}%
          </button>
          <button
            type="button"
            class="image-lightbox-btn"
            title="Zoom in"
            :disabled="zoom >= MAX_ZOOM"
            @click="zoomBy(ZOOM_STEP)"
          >
            <i class="fas fa-magnifying-glass-plus"></i>
          </button>
          <a
            :href="currentUrl"
            download
            class="image-lightbox-btn"
            title="Download"
          >
            <i class="fas fa-download"></i>
          </a>
          <button
            type="button"
            class="image-lightbox-btn"
            title="Close"
            @click="$emit('close')"
          >
            <i class="fas fa-xmark"></i>
          </button>
        </div>
      </div>

      <div
        ref="stage"
        class="image-lightbox-stage"
        @click.self="$emit('close')"
        @wheel.ctrl.prevent="zoomBy($event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP)"
      >
        <div v-if="failed" class="image-lightbox-status">
          <i class="fas fa-exclamation-circle"></i>
          This chart is no longer available.
        </div>
        <img
          v-else
          ref="img"
          :src="currentUrl"
          :alt="alt"
          :style="imageStyle"
          @load="measureFit"
          @error="handleError"
        />
      </div>
    </div>
  </Teleport>
</template>

<script>
import oliverClient from "@/lib/OliverClient";

const MIN_ZOOM = 50;
const MAX_ZOOM = 400;
const ZOOM_STEP = 25;

export default {
  name: "ImageLightbox",
  props: {
    url: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: "",
    },
    /**
     * The artifact this image came from, when it has one. A chart's URL is
     * presigned and expires, so the viewer needs the id to mint a live one
     * rather than show a broken image on a conversation left open a while.
     */
    artifactId: {
      type: String,
      default: null,
    },
  },
  emits: ["close"],
  data() {
    return {
      MIN_ZOOM,
      MAX_ZOOM,
      ZOOM_STEP,
      zoom: 100,
      // Width in px at which the whole chart fits the stage; 100% zoom. Measured
      // rather than expressed as a percentage because a percentage width resolves
      // against the stage's WIDTH alone, which lets a tall chart overflow the
      // bottom and forces a scroll to see a chart the viewer was opened to show.
      fitWidth: 0,
      // Set once the URL has been re-minted, so the original prop stays intact.
      mintedUrl: null,
      failed: false,
    };
  },
  computed: {
    currentUrl() {
      return this.mintedUrl || this.url;
    },
    imageStyle() {
      // Until the image has loaded there is nothing to measure, so the CSS
      // contains it and no zoom applies yet.
      if (!this.fitWidth) return {};
      return {
        width: `${Math.round((this.fitWidth * this.zoom) / 100)}px`,
        maxWidth: "none",
        maxHeight: "none",
      };
    },
  },
  mounted() {
    this.previouslyFocused = document.activeElement;
    this.$refs.root?.focus();

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    if (window.ResizeObserver) {
      this.stageObserver = new ResizeObserver(() => this.measureFit());
      this.stageObserver.observe(this.$refs.stage);
    }
  },
  beforeUnmount() {
    this.stageObserver?.disconnect();
    document.body.style.overflow = this.previousBodyOverflow || "";
    this.previouslyFocused?.focus?.();
  },
  methods: {
    zoomBy(delta) {
      this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.zoom + delta));
    },
    /**
     * Size at which the whole chart is visible without scrolling. Never above
     * 1:1 — a chart is drawn at 1500px+ and upscaling it past its own pixels
     * only makes it blurry.
     */
    measureFit() {
      const img = this.$refs.img;
      const stage = this.$refs.stage;
      if (!img?.naturalWidth || !stage) return;

      const style = getComputedStyle(stage);
      const availableWidth =
        stage.clientWidth -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight);
      const availableHeight =
        stage.clientHeight -
        parseFloat(style.paddingTop) -
        parseFloat(style.paddingBottom);
      if (availableWidth <= 0 || availableHeight <= 0) return;

      const scale = Math.min(
        1,
        availableWidth / img.naturalWidth,
        availableHeight / img.naturalHeight
      );
      this.fitWidth = img.naturalWidth * scale;
    },
    /**
     * Re-mint once. The image on screen was loading a moment ago, so a failure
     * here is an expired URL rather than a missing file — but a second failure
     * is the file itself, and retrying it would loop on its own error event.
     */
    async handleError() {
      if (!this.artifactId || this.mintedUrl) {
        this.failed = true;
        return;
      }
      try {
        const { data } = await oliverClient.getAttachment(this.artifactId);
        if (data?.url) {
          this.mintedUrl = data.url;
          return;
        }
      } catch (error) {
        console.error("Attachment fetch failed", this.artifactId, error);
      }
      this.failed = true;
    },
  },
};
</script>

<style lang="scss" scoped>
// Viewport-level, above everything. Oliver's own panel is 9999, but the app has
// existing 99999 overlays (print reports, the import screens), so clearing 9999
// alone would still let one of those cover the viewer.
.image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  background: rgba(12, 10, 9, 0.95);

  &:focus {
    outline: none;
  }
}

.image-lightbox-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.image-lightbox-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-lightbox-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.image-lightbox-zoom {
  min-width: 52px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    color: #fff;
  }
}

.image-lightbox-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
    background: rgba(255, 255, 255, 0.1);
  }
}

.image-lightbox-stage {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  padding: 16px;

  img {
    // `margin: auto` centres on both axes and, unlike justify/align-content,
    // keeps the top-left reachable once a zoomed chart overflows the stage.
    margin: auto;
    // Sized explicitly by `imageStyle`, so the image must not be allowed to
    // flex: the default `flex-shrink: 1` squashes a zoomed chart back to the
    // stage width, which is why zooming in appeared to do nothing at all.
    flex: none;
    max-width: 100%;
    max-height: 100%;
    height: auto;
    border-radius: 8px;
    background: #fff;
  }
}

.image-lightbox-status {
  margin: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
}
</style>
